import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import {
  useHandlers,
  withPageProviders,
} from '@sparklayer-io/spark-dash-pages/context/page-context';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Heading, Page, Pressable, Skeleton, Text } from '@ui/themed';
import { useStaffApi } from '../hooks/use-staff-api';
import type { StaffActionCategory } from '../types';

type ManageActionsPageConfig = {
  category: StaffActionCategory;
  title: string;
  subtitle: (teamName: string) => string;
};

function ManageActionsPageInner({ category, title, subtitle }: ManageActionsPageConfig) {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const { staffApi } = useStaffApi();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['staff-actions', env.team.id, category],
    queryFn: () => staffApi.actions(env.team.id, category),
  });

  if (isLoading) return <Skeleton />;

  if (isError || !data) {
    return (
      <Alert variant="error" headingLevel="h3">
        There was a problem loading these actions.
      </Alert>
    );
  }

  const { allowed: allowedActions, unavailable: unavailableActions } = data.actions;

  return (
    <Page title={title} description={subtitle(env.team.name)}>
      <Card>
        <div className="flex flex-col gap-8">
          {allowedActions.length === 0 && (
            <Alert variant="warning" headingLevel="h3">
              There are no actions available for this team.
            </Alert>
          )}
          <div>
            {allowedActions.map((action) => (
              <div
                key={action.type}
                className="border-spark-border-subtle border-s border-e border-t last:border-b"
              >
                <Pressable
                  onClick={() =>
                    navigationHandler.navigate(`/staff/${category}/${action.type}`, {
                      excludeSiteEnv: true,
                    })
                  }
                  fullWidth
                >
                  <div className="w-full p-4 text-start">
                    <Heading level="h3" style="sm" weight="semibold">
                      {action.name}
                    </Heading>
                    <Text variant="secondary" size="sm">
                      {action.description}
                    </Text>
                  </div>
                </Pressable>
              </div>
            ))}
          </div>
          {unavailableActions.length > 0 && (
            <div>
              <div className="mb-2 flex flex-col gap-2">
                <Heading level="h3" style="md">
                  Unavailable Actions
                </Heading>
                <Text variant="secondary">
                  The following actions are not available at this time for this team
                </Text>
              </div>
              {unavailableActions.map((action) => (
                <div
                  key={action.type}
                  className="border-spark-border-subtle border-s border-e border-t last:border-b"
                >
                  <div className="w-full bg-spark-surface-muted p-4 text-start">
                    <Heading level="h3" style="sm" weight="semibold">
                      {action.name} —{' '}
                      <span className="text-sm text-spark-warning-soft-text italic">
                        {action.reason}
                      </span>
                    </Heading>
                    <Text variant="secondary" size="sm">
                      {action.description}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </Page>
  );
}

export const ManageBillingPage = withPageProviders(() => (
  <ManageActionsPageInner
    category="manage-billing"
    title="Manage Billing"
    subtitle={(name) => `Manage the billing for ${name}`}
  />
));

export const ManageTeamPage = withPageProviders(() => (
  <ManageActionsPageInner
    category="manage-team"
    title="Manage Team"
    subtitle={(name) => `Manage team ${name}`}
  />
));
